import { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getCompany, updateCompany } from "services/Company.services";
import { toast } from "sonner";
import { z } from "zod";

import type { Company } from "types/Company.type";

import { queryClient } from "utils/queryClient";

import { buildClinicSchema } from "./Clinic.schema";

export default function useClinic() {
  const { t } = useTranslation();

  const { data: clinic, isLoading: isClinicLoading } = useQuery<Company>({
    queryKey: ["clinic"],
    queryFn: () => getCompany(),
  });

  const updateClinicMutation = useMutation<Company, Error, Company>({
    mutationFn: (clinic) => updateCompany(clinic),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinic"] });
      toast.success(t("profile.clinic.updateSuccess"), {
        description: t("profile.clinic.updateSuccessDescription"),
      });
    },
  });

  const clinicSchema = buildClinicSchema(t);

  const form = useForm<z.infer<typeof clinicSchema>>({
    resolver: zodResolver(clinicSchema),
    defaultValues: {
      name: "",
      address: "",
      description: "",
      website: "https://",
      email: "",
      phone: "",
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  function onSubmit(values: z.infer<typeof clinicSchema>) {
    updateClinicMutation.mutate(values);
  }

  useEffect(() => {
    if (clinic) {
      reset(clinic);
    }
  }, [clinic, reset]);

  return { clinic, isClinicLoading, updateClinicMutation, form, onSubmit, handleSubmit, isDirty };
}
